import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SketchPicker } from 'react-color';

import { makeStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

import IconsSend from '@mui/icons-material/Send';
import CircularProgress from '@mui/material/CircularProgress';
import { ApolloError } from '@apollo/client';
import { Theme } from '@mui/material/styles';
import MinisiteLayout from '../../../layout/Minisite';
import ButtonSubmit from '../../../components/_button/ButtonSubmit';
import useNotification from '../../../components/_hook/useNotification';
import useRouter from '../../../components/_hook/useRouter';
import useForm from '../../../components/_hook/useForm';
import { useMinisiteQuery } from '../../../../graphql/query/minisite/useMinisiteQuery';
import MINISITE from '../../../components/constant/minisite';
import { useUpdateMinisiteThemeMutation } from '../../../../graphql/mutation/minisite/minisiteTheme/updateMinisiteThemeMutation';
import NotFound from '../../../components/_error/NotFound';
import Helmet from '../../../components/Helmet';
import Context from '../../../context';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
  },
}));

function MinisiteTheme() {
  interface IFormValue {
    primaryColor: string;
    secondaryColor: string;
  }

  const classes = useStyles();
  const { t } = useTranslation();
  const { notification } = useNotification();
  const { params } = useRouter();
  const { company } = useContext(Context);
  const { docID: minisiteId } = params;
  const [
    primaryColorAnchorEl,
    setPrimaryColorAnchorEl,
  ] = useState<Element | null>(null);
  const [
    secondaryColorAnchorEl,
    setSecondaryColorAnchorEl,
  ] = useState<Element | null>(null);

  const { data, loading } = useMinisiteQuery(
    `
			_id
            theme {
              primaryColor
              secondaryColor
            }
		`,
    {
      variables: {
        filter: {
          companyId: company._id,
          _id: minisiteId as string,
        },
      },
      skip: minisiteId === 'new',
    }
  );

  function onCompleted() {
    notification.success(t('success'));
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    updateMinisiteThemeMutation,
    { loading: isUpdatingMinisiteTheme },
  ] = useUpdateMinisiteThemeMutation(
    `updatedMinisite {
				_id
                theme {
                  primaryColor
                  secondaryColor
                }
			}`,
    {
      onCompleted,
      onError,
    }
  );

  async function onSubmitForm(values: IFormValue) {
    if (data) {
      await updateMinisiteThemeMutation({
        variables: {
          _id: data.minisites.items[0]._id,
          primaryColor: values.primaryColor,
          secondaryColor: values.secondaryColor,
        },
      });
    }
  }

  const { values, setValue, handleSubmit, setValues } = useForm<IFormValue>({
    defaultValues: {
      primaryColor: MINISITE.DEFAULT_THEME.PRIMARY_COLOR,
      secondaryColor: MINISITE.DEFAULT_THEME.SECONDARY_COLOR,
    },
    onSubmit: onSubmitForm,
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};

      if (!tempValues.primaryColor) {
        newErrors.primaryColor = t('__type__is_required', {
          type: t('primary_color'),
        });
      }

      if (!tempValues.secondaryColor) {
        newErrors.secondaryColor = t('__type__is_required', {
          type: t('secondary_color'),
        });
      }

      Object.keys(newErrors).forEach((newError) =>
        notification.error(newErrors[newError])
      );

      return newErrors;
    },
  });

  useEffect(() => {
    if (!loading && data && data.minisites.items.length) {
      const minisite = data.minisites.items[0];

      if (minisite.theme) {
        setValues({
          primaryColor:
            minisite.theme.primaryColor || MINISITE.DEFAULT_THEME.PRIMARY_COLOR,
          secondaryColor:
            minisite.theme.secondaryColor ||
            MINISITE.DEFAULT_THEME.SECONDARY_COLOR,
        });
      }
    }
  }, [loading]);

  if (loading && minisiteId) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  const minisite = minisiteId && data ? data.minisites.items[0] : null;

  if (!minisite) {
    return <NotFound />;
  }

  return (
    <Paper className={classes.paper}>
      <Helmet title={`${t('theme')} | ${t('minisite')}`} />
      <Grid container item xs={12} spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">
            {t('minisite')} {t('theme')}
          </Typography>
        </Grid>
        <Grid container item xs={12} spacing={2}>
          <Grid container item xs={12} spacing={1}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">{t('primary_color')}</Typography>
            </Grid>
            <Grid container item xs={12} spacing={1}>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={(event) => {
                    setPrimaryColorAnchorEl(event.currentTarget);
                  }}
                  style={{ backgroundColor: values.primaryColor }}
                >
                  {values.primaryColor}
                </Button>
                <Popover
                  open={Boolean(primaryColorAnchorEl)}
                  anchorEl={primaryColorAnchorEl}
                  onClose={() => setPrimaryColorAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <SketchPicker
                    color={values.primaryColor}
                    onChange={(color) => setValue('primaryColor', color.hex)}
                  />
                </Popover>
              </Grid>
              <Grid item>
                <Button
                  variant="text"
                  onClick={() => {
                    setValue(
                      'primaryColor',
                      MINISITE.DEFAULT_THEME.PRIMARY_COLOR
                    );
                  }}
                >
                  {t('reset')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={1}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">
                {t('secondary_color')}
              </Typography>
            </Grid>
            <Grid container item xs={12} spacing={1}>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={(event) => {
                    setSecondaryColorAnchorEl(event.currentTarget);
                  }}
                  style={{ backgroundColor: values.secondaryColor }}
                >
                  {values.secondaryColor}
                </Button>
                <Popover
                  open={Boolean(secondaryColorAnchorEl)}
                  anchorEl={secondaryColorAnchorEl}
                  onClose={() => setSecondaryColorAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <SketchPicker
                    color={values.secondaryColor}
                    onChange={(color) => setValue('secondaryColor', color.hex)}
                  />
                </Popover>
              </Grid>
              <Grid item>
                <Button
                  variant="text"
                  onClick={() => {
                    setValue(
                      'secondaryColor',
                      MINISITE.DEFAULT_THEME.SECONDARY_COLOR
                    );
                  }}
                >
                  {t('reset')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container spacing={1} justifyContent="flex-end">
          <Grid item xs={3} md={2}>
            <ButtonSubmit
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              isSubmitting={isUpdatingMinisiteTheme}
              label={t('save')}
              icon={<IconsSend />}
            />
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default () => <MinisiteLayout main={<MinisiteTheme />} />;
